/**
 * Pure geometry for the Labs hex mark: pointy-top hex vertices + annular sectors with scooped outer edges.
 * Vertex 0 is at the top (−90° from +x, same convention as LabsDeonticLogo).
 */

function fmt(n) {
  return n.toFixed(2);
}

/** @param {number} deg — 0 = east, 90 = south (SVG y-down). */
function labsPolarPoint(cx, cy, r, deg) {
  const t = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(t), y: cy + r * Math.sin(t) };
}

/** Azimuth (degrees) of pointy-top hex vertex `i` (0 = top). */
function labsHexVertexAzimuthDeg(i) {
  return -90 + i * 60;
}

/**
 * @param {number} cx
 * @param {number} cy
 * @param {number} circumRadius
 * @returns {{ x: number, y: number }[]}
 */
export function labsHexVerticesPointyTop(cx, cy, circumRadius) {
  const verts = [];
  for (let i = 0; i < 6; i += 1) {
    const a = -Math.PI / 2 + (i * Math.PI) / 3;
    verts.push({
      x: cx + circumRadius * Math.cos(a),
      y: cy + circumRadius * Math.sin(a),
    });
  }
  return verts;
}

/**
 * One sector: inner hex edge Vi→Vi+1, outer hex edge with quadratic bow toward center.
 * @param {number} cx
 * @param {number} cy
 * @param {{ x: number, y: number }[]} innerVerts
 * @param {{ x: number, y: number }[]} outerVerts
 * @param {number} edgeIndex - sector between vertex edgeIndex and edgeIndex+1 (mod 6)
 * @param {number} indentTowardCenter
 */
export function labsHexSectorPath(cx, cy, innerVerts, outerVerts, edgeIndex, indentTowardCenter = 0.14) {
  const i = edgeIndex;
  const j = (i + 1) % 6;
  const i0 = innerVerts[i];
  const i1 = innerVerts[j];
  const o0 = outerVerts[i];
  const o1 = outerVerts[j];
  const mx = (o0.x + o1.x) / 2;
  const my = (o0.y + o1.y) / 2;
  const qx = mx + indentTowardCenter * (cx - mx);
  const qy = my + indentTowardCenter * (cy - my);
  return `M ${fmt(i0.x)} ${fmt(i0.y)} L ${fmt(o0.x)} ${fmt(o0.y)} Q ${fmt(qx)} ${fmt(qy)} ${fmt(o1.x)} ${fmt(o1.y)} L ${fmt(i1.x)} ${fmt(i1.y)} Z`;
}

/**
 * Sector with circular hub void: inner boundary is a 60° arc; outer edge matches {@link labsHexSectorPath}.
 */
export function labsHexSectorPathCircularHub(cx, cy, holeR, outerVerts, edgeIndex, indentTowardCenter = 0.14) {
  const i = edgeIndex;
  const j = (i + 1) % 6;
  const a0 = labsHexVertexAzimuthDeg(i);
  const a1 = labsHexVertexAzimuthDeg(j);
  const innerStart = labsPolarPoint(cx, cy, holeR, a0);
  const innerEnd = labsPolarPoint(cx, cy, holeR, a1);
  const o0 = outerVerts[i];
  const o1 = outerVerts[j];
  const mx = (o0.x + o1.x) / 2;
  const my = (o0.y + o1.y) / 2;
  const qx = mx + indentTowardCenter * (cx - mx);
  const qy = my + indentTowardCenter * (cy - my);
  return `M ${fmt(innerStart.x)} ${fmt(innerStart.y)} L ${fmt(o0.x)} ${fmt(o0.y)} Q ${fmt(qx)} ${fmt(qy)} ${fmt(o1.x)} ${fmt(o1.y)} L ${fmt(innerEnd.x)} ${fmt(innerEnd.y)} A ${fmt(holeR)} ${fmt(holeR)} 0 0 0 ${fmt(innerStart.x)} ${fmt(innerStart.y)} Z`;
}

/**
 * Midpoint of the outer hex edge from vertex i to i+1 (tick anchor on the rim).
 */
export function labsHexOuterEdgeMidpoint(outerVerts, edgeIndex) {
  const i = edgeIndex;
  const j = (i + 1) % 6;
  const o0 = outerVerts[i];
  const o1 = outerVerts[j];
  return { x: (o0.x + o1.x) / 2, y: (o0.y + o1.y) / 2 };
}

/**
 * @param {{ x: number, y: number }[]} verts
 * @returns {string} SVG polygon `points` attribute value
 */
export function labsHexPointsAttribute(verts) {
  return verts.map((v) => `${fmt(v.x)},${fmt(v.y)}`).join(' ');
}

/**
 * Short stroke from outer hex vertex radially outward (bezel / indexing mark).
 * @returns {{ x1: number, y1: number, x2: number, y2: number }}
 */
export function labsVertexOutwardTick(cx, cy, vx, vy, length) {
  const rx = vx - cx;
  const ry = vy - cy;
  const L = Math.hypot(rx, ry);
  const ux = rx / L;
  const uy = ry / L;
  return {
    x1: vx,
    y1: vy,
    x2: vx + ux * length,
    y2: vy + uy * length,
  };
}

/**
 * Major inward tick at edge midpoint plus two shorter subdivision ticks (readout / spectrum metaphor).
 * Radial direction approximated from rim midpoint toward center; minors offset along the chord.
 */
export function labsOuterEdgeInstrumentTicks(
  cx,
  cy,
  outerVerts,
  edgeIndex,
  { majorInward, minorInward, chordOffset },
) {
  const i = edgeIndex;
  const j = (i + 1) % 6;
  const o0 = outerVerts[i];
  const o1 = outerVerts[j];
  const mx = (o0.x + o1.x) / 2;
  const my = (o0.y + o1.y) / 2;
  let tx = o1.x - o0.x;
  let ty = o1.y - o0.y;
  const tLen = Math.hypot(tx, ty);
  tx /= tLen;
  ty /= tLen;
  const rx = cx - mx;
  const ry = cy - my;
  const rLen = Math.hypot(rx, ry);
  const ux = rx / rLen;
  const uy = ry / rLen;

  const major = { x1: mx, y1: my, x2: mx + ux * majorInward, y2: my + uy * majorInward };

  const mkMinor = (px, py) => ({
    x1: px,
    y1: py,
    x2: px + ux * minorInward,
    y2: py + uy * minorInward,
  });

  return [
    major,
    mkMinor(mx - tx * chordOffset, my - ty * chordOffset),
    mkMinor(mx + tx * chordOffset, my + ty * chordOffset),
  ];
}
